import React, { useEffect, useMemo, useState } from 'react';
import { discounts, DiscountTypes, shops, ShopTypes } from 'api';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ErrorBoundary, Loader } from 'components';
import { useStoreContext } from 'store';
import { getCurrentDate } from 'helpers/dayJsFormats';
import Form from './Form';
import {
  dataInitialSelectsOptions,
  dataToInitialModel,
  initialModel,
  dataToCloneModel,
} from './fields';

type Params = 'id';

interface Props {
  itemId?: string;
}

const Index = (props: Props): JSX.Element | null => {
  const { itemId } = props;
  const { id: paramsId = '' } = useParams<Params>();
  const {
    state: { user, shops: storeShops },
  } = useStoreContext();

  const id = useMemo(() => paramsId || itemId || '', [itemId, paramsId]);

  const [searchParams] = useSearchParams();

  const cloneDiscountId = useMemo(() => {
    const parsed = Object.fromEntries(searchParams);

    return 'cloneDiscountId' in parsed ? parsed.cloneDiscountId : undefined;
  }, [searchParams]);

  const [initialData, setInitialData] = useState<
    DiscountTypes.ItemCreateParams | undefined
  >(undefined);
  const [initialSelectsOptions, setInitialSelectsOptions] = useState<
    ReturnType<typeof dataInitialSelectsOptions> | undefined
  >(undefined);
  const [selectedShop, setSelectedShop] = useState<
    ShopTypes.ItemData | undefined
  >();

  const { data, error } = useQuery(
    [discounts.endpoints.getById(cloneDiscountId || id)],
    () => discounts.getById(cloneDiscountId || id),
    {
      enabled: !!(cloneDiscountId || id),
      cacheTime: 0,
    },
  );

  const { data: shopsData } = useQuery(
    [shops.endpoints.getFlatList],
    shops.getFlatList,
    {
      refetchOnWindowFocus: false,
      initialData: storeShops,
      enabled: storeShops.length === 0,
    },
  );

  useEffect(() => {
    if (!id && !cloneDiscountId) {
      setInitialData({ ...initialModel, start_date: getCurrentDate() });
    }

    if (!id && cloneDiscountId && data) {
      const initialFormData = dataToCloneModel(data);
      const initialSelectsData = dataInitialSelectsOptions(data);

      setSelectedShop(
        shopsData?.find((s) => s._id.$oid === initialFormData?.shop_id),
      );
      setInitialData(initialFormData);
      setInitialSelectsOptions(initialSelectsData);
    }

    if (id && data) {
      const initialFormData = dataToInitialModel(data);
      const initialSelectsData = dataInitialSelectsOptions(data);

      setSelectedShop(
        shopsData?.find((s) => s._id.$oid === initialFormData?.shop_id),
      );
      setInitialData(initialFormData);
      setInitialSelectsOptions(initialSelectsData);
    }
  }, [cloneDiscountId, data, id, shopsData, user]);

  if (error) {
    return <ErrorBoundary variant="error" />;
  }

  return !initialData ? (
    <Loader height="80vh" variant="centered" />
  ) : (
    <Form
      drawer={!!itemId}
      drawerTitle={`Edit ${data?.coupon_code || 'discount'}`}
      id={id}
      cloneDiscountId={cloneDiscountId}
      initialData={initialData}
      initialSelectsOptions={initialSelectsOptions}
      selectedShop={selectedShop}
      setSelectedShop={setSelectedShop}
    />
  );
};

export default Index;
